import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Group, Loader, Paper, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useLessons } from './store';

// ---------------------------------------------------------------------------
// LessonOverlay — the guided-tour engine.
//
// While a lesson is active it renders four fixed "blocker" panes around the
// spotlighted control, so the ONLY interactive area of the screen is the
// control the current step points at (strict mode). The user really clicks the
// real button — nothing is simulated. A tooltip card next to the spotlight
// carries the instruction and the step counter.
//
// The engine re-measures the target on a short interval instead of observing
// events: it survives scrolling, resizing, route changes, modals mounting in
// portals, and Mantine animations without any wiring into the pages themselves.
// ---------------------------------------------------------------------------

const Z_BLOCK = 9000; // above Mantine modals (~200) and menus (~300)
const PAD = 6;        // spotlight padding around the target rect
const TICK_MS = 120;
const STABLE_TICKS = 2; // target must hold position this many extra ticks before the spotlight shows

interface Rect { top: number; left: number; width: number; height: number }

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height)
  );
}

function isVisible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

/** Read the current value of the spotlighted input (target may be a wrapper). */
function inputValueOf(el: Element | null): string {
  if (!el) return '';
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value;
  const inner = el.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
  return inner?.value ?? '';
}

const blockerStyle: React.CSSProperties = {
  position: 'fixed',
  background: 'rgba(15, 17, 21, 0.55)',
  // The blur makes the blocked area read as "inactive" even in environments
  // that tone-map dark overlays (auto-dark emulation, forced colors).
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter: 'blur(2px)',
  zIndex: Z_BLOCK,
  // No position/size transition: the blocker panes must snap to the spotlight
  // hole every tick. If they animated, a moving target (e.g. a button's :active
  // press transform) would let a pane sweep across the hole and swallow the
  // click.
};

export default function LessonOverlay() {
  const { active, stepIndex, advance, skip, exit } = useLessons();
  const step = active?.steps[stepIndex] ?? null;

  const [rect, setRect] = useState<Rect | null>(null);
  const [found, setFound] = useState(false);
  const [inputOk, setInputOk] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  const targetRef = useRef<Element | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const scrolledToRef = useRef<Element | null>(null);
  const advancedAtStepRef = useRef(-1);
  // Last raw measurement + how many consecutive ticks it has held, used to
  // detect when a target has stopped moving (modal open/scroll animations)
  // before we reveal the spotlight.
  const lastMeasureRef = useRef<Rect | null>(null);
  const stableCountRef = useRef(0);
  // Once a target has been revealed for the current step, we stop re-gating on
  // movement: the hole stays open and simply follows the target. Without this,
  // a button's :active press transform (or any 1px jitter) would reset the gate
  // and briefly slam a full-screen blocker over the control, eating the click.
  const revealedRef = useRef(false);

  const isLastStep = !!active && stepIndex === active.steps.length - 1;

  // Reset transient state on step change.
  useEffect(() => {
    setConfirmExit(false);
    setInputOk(false);
    setFound(false);
    setRect(null);
    targetRef.current = null;
    scrolledToRef.current = null;
    lastMeasureRef.current = null;
    stableCountRef.current = 0;
    revealedRef.current = false;
    // Allow one advance on the new step (guards against a stale value blocking
    // a click-advance when a lesson is restarted onto a previously-seen index).
    advancedAtStepRef.current = -1;
  }, [active?.id, stepIndex]);

  // Main measurement/advance tick.
  useEffect(() => {
    if (!active || !step) return;
    const tick = () => {
      // Auto steps: advance the moment their condition is met.
      if (step.advance.kind === 'auto' && step.advance.when()) {
        if (advancedAtStepRef.current !== stepIndex) {
          advancedAtStepRef.current = stepIndex;
          doAdvance();
        }
        return;
      }
      if (!step.target) { setFound(true); setRect(null); return; }
      const el = typeof step.target === 'function' ? step.target() : document.querySelector(step.target);
      if (el && isVisible(el)) {
        targetRef.current = el;
        if (scrolledToRef.current !== el) {
          scrolledToRef.current = el;
          // Instant (not smooth) scroll: a smooth animation makes the spotlight
          // "chase" the moving target for ~300ms. Instant puts the control in
          // place so the ring locks on immediately.
          try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch { /* older browsers */ }
        }
        const r = el.getBoundingClientRect();
        const next = { top: r.top, left: r.left, width: r.width, height: r.height };
        // Gate only the FIRST reveal: wait until the target has held the same
        // position for a few consecutive ticks (~STABLE_TICKS × TICK_MS) so the
        // spotlight doesn't flash in the wrong place while a Mantine modal is
        // still scaling/fading in or scrolling into view (that motion is NOT
        // monotonic — two early frames can match, so one match isn't enough).
        // Once revealed, we keep the hole open and simply follow the target, so
        // later movement (a button's :active press transform, a 1px reflow)
        // never re-blocks the control mid-click.
        if (sameRect(lastMeasureRef.current, next)) stableCountRef.current += 1;
        else stableCountRef.current = 0;
        lastMeasureRef.current = next;
        if (revealedRef.current || stableCountRef.current >= STABLE_TICKS) {
          revealedRef.current = true;
          setFound(true);
          setRect((prev) => (sameRect(prev, next) ? prev : next));
          if (step.advance.kind === 'input') setInputOk(step.advance.valid(inputValueOf(el)));
        } else {
          setFound(false);
        }
      } else {
        // Target genuinely gone (unmounted / navigated). Re-gate if it returns.
        targetRef.current = null;
        lastMeasureRef.current = null;
        stableCountRef.current = 0;
        revealedRef.current = false;
        setFound(false);
      }
    };
    tick();
    const t = setInterval(tick, TICK_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, stepIndex, step]);

  // Click-to-advance: listen in capture phase so we advance even when the
  // clicked control immediately unmounts (menu items, modal Save buttons).
  useEffect(() => {
    if (!active || !step || step.advance.kind !== 'click') return;
    const onClick = (e: MouseEvent) => {
      const el = targetRef.current;
      if (!el || advancedAtStepRef.current === stepIndex) return;
      if (e.target instanceof Node && el.contains(e.target)) {
        advancedAtStepRef.current = stepIndex;
        // Let the real click handlers run first, then move the lesson on.
        setTimeout(() => doAdvance(), 80);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, stepIndex, step]);

  // Keyboard guard: during a strict lesson, Escape (would close Mantine modals
  // mid-flow) and stray Enter presses are swallowed. Enter is still allowed
  // inside the spotlighted control (e.g. submitting a field) AND inside the
  // tour's own instruction card, so keyboard users can activate Next / Finish /
  // Exit with Enter rather than only Space.
  useEffect(() => {
    if (!active || !step || step.soft) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); return; }
      if (e.key === 'Enter') {
        const t = e.target instanceof Node ? e.target : null;
        const inTarget = !!(targetRef.current && t && targetRef.current.contains(t));
        const inCard = !!(t && cardRef.current?.contains(t));
        if (!inTarget && !inCard) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [active?.id, stepIndex, step]);

  function doAdvance() {
    if (!active) return;
    if (stepIndex === active.steps.length - 1) {
      notifications.show({
        title: 'Lesson complete 🎓',
        message: `“${active.title}” is done — nice work! Find the next lesson on the Lessons page.`,
        color: 'green',
        autoClose: 6000,
      });
    }
    advance();
  }

  const block = (e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); };

  // Tooltip placement: below the spotlight when there is room, else above.
  const cardPos = useMemo((): React.CSSProperties => {
    if (!rect) {
      return { position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: Z_BLOCK + 2 };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(360, vw - 24);
    const left = Math.max(12, Math.min(rect.left, vw - width - 12));
    const below = rect.top + rect.height + PAD + 12;
    if (below + 230 <= vh) {
      return { position: 'fixed', left, top: below, width, zIndex: Z_BLOCK + 2 };
    }
    return {
      position: 'fixed', left, top: Math.max(12, rect.top - PAD - 12), width,
      transform: 'translateY(-100%)', zIndex: Z_BLOCK + 2,
    };
  }, [rect]);

  if (!active || !step) return null;

  const strict = !step.soft;
  const showSpotlight = !!step.target && found && !!rect;
  const waiting = !!step.target && !found;

  const hole = showSpotlight && rect
    ? {
        top: Math.max(0, rect.top - PAD),
        left: Math.max(0, rect.left - PAD),
        right: Math.min(window.innerWidth, rect.left + rect.width + PAD),
        bottom: Math.min(window.innerHeight, rect.top + rect.height + PAD),
      }
    : null;

  return (
    <>
      {/* Blocker panes: everything except the spotlight hole is inert. */}
      {strict && hole && (
        <>
          <div style={{ ...blockerStyle, top: 0, left: 0, right: 0, height: hole.top }}
            onClickCapture={block} onMouseDownCapture={block} onPointerDownCapture={block} onContextMenu={block} />
          <div style={{ ...blockerStyle, top: hole.bottom, left: 0, right: 0, bottom: 0 }}
            onClickCapture={block} onMouseDownCapture={block} onPointerDownCapture={block} onContextMenu={block} />
          <div style={{ ...blockerStyle, top: hole.top, left: 0, width: hole.left, height: hole.bottom - hole.top }}
            onClickCapture={block} onMouseDownCapture={block} onPointerDownCapture={block} onContextMenu={block} />
          <div style={{ ...blockerStyle, top: hole.top, left: hole.right, right: 0, height: hole.bottom - hole.top }}
            onClickCapture={block} onMouseDownCapture={block} onPointerDownCapture={block} onContextMenu={block} />
        </>
      )}
      {/* Full dim while waiting for the next control, or for target-less steps. */}
      {strict && !hole && (
        <div style={{ ...blockerStyle, inset: 0 }}
          onClickCapture={block} onMouseDownCapture={block} onPointerDownCapture={block} onContextMenu={block} />
      )}

      {/* Spotlight ring. */}
      {showSpotlight && rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            border: '2px solid var(--mantine-color-orange-5)',
            borderRadius: 8,
            boxShadow: '0 0 0 4px rgba(253, 126, 20, 0.25), 0 0 18px rgba(253, 126, 20, 0.45)',
            zIndex: Z_BLOCK + 1,
            pointerEvents: 'none',
            // Snap to the target (no position transition) so it always sits on
            // the hole; a transition would let a reflow catch the ring mid-flight
            // in the wrong place. Only the glow fades.
            transition: 'box-shadow 120ms ease',
          }}
        />
      )}

      {/* Instruction card. */}
      <Paper ref={cardRef} shadow="xl" radius="md" p="md" withBorder style={cardPos}>
        <Group justify="space-between" mb={6} wrap="nowrap">
          <Badge variant="light" color="orange">Step {stepIndex + 1} / {active.steps.length}</Badge>
          <Text size="xs" c="dimmed" truncate style={{ maxWidth: 170 }}>{active.title}</Text>
        </Group>
        <Text fw={700} mb={4}>{waiting ? 'One moment…' : step.title}</Text>
        <div style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.5 }}>
          {waiting
            ? <Group gap="xs"><Loader size="xs" /><Text size="sm">Waiting for the app — this appears once the previous action finishes.</Text></Group>
            : step.body}
        </div>
        {!waiting && step.advance.kind === 'click' && (
          <Text size="xs" c="orange.6" fw={600} mt={8}>👆 Click the highlighted control to continue</Text>
        )}
        {!waiting && step.advance.kind === 'input' && step.advance.hint && (
          <Text size="xs" c="dimmed" mt={6}>{step.advance.hint}</Text>
        )}
        <Group justify="space-between" mt="sm">
          {confirmExit ? (
            <Group gap="xs">
              <Button size="compact-xs" color="red" onClick={() => { setConfirmExit(false); exit(); }}>
                Yes, exit
              </Button>
              <Button size="compact-xs" variant="default" onClick={() => setConfirmExit(false)}>
                Keep going
              </Button>
            </Group>
          ) : (
            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setConfirmExit(true)}>
              Exit lesson
            </Button>
          )}
          <Group gap="xs">
            {step.optional && (
              <Button size="compact-sm" variant="default" onClick={() => skip()}>Skip</Button>
            )}
            {!waiting && (step.advance.kind === 'next' || step.advance.kind === 'input') && (
              <Button
                size="compact-sm"
                disabled={step.advance.kind === 'input' && !inputOk}
                onClick={() => doAdvance()}
              >
                {isLastStep ? 'Finish 🎉' : 'Next'}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>
    </>
  );
}
